'use client';

import type { ElementType } from 'react';
import { LucideIcon } from '@skin-seoul/design-system';
import type { Currency, Product } from '@skin-seoul/service';
import { CartHorizontalScroll } from './CartHorizontalScroll';
import { CartDiscoveryProductCard } from './CartDiscoveryProductCard';
import { useCartDiscovery } from './CartDiscoveryNavigation';
import { CartSectionAnalytics, useCartAnalytics } from './CartAnalytics';

interface Props {
  products: Array<{ product: Product; rating?: { averageRate: number; totalReviews: number } }>;
  onCartMutation?: (mutation: Promise<boolean>) => void;
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
}

/** Presentation only: callers must supply actual analysis results, never a cross-sell fallback. */
export function CartRoutineProducts({ products, ...cardProps }: Props) {
  const { open } = useCartDiscovery();
  const { track } = useCartAnalytics();
  if (!products.length) {
    return null;
  }
  const renderCards = (layout: 'compact' | 'grid' = 'compact') =>
    products.map(({ product, rating }, index) => (
      <CartDiscoveryProductCard
        key={product.sku}
        product={product}
        rating={rating}
        layout={layout}
        {...cardProps}
        attribution={{ placement: 'cart_skin_routine', position: index, isDetail: layout === 'grid' }}
      />
    ));
  return (
    <CartSectionAnalytics placement="cart_skin_routine" count={products.length}>
      <section aria-label="Your Skin Analysis routine" className="-mx-4 border-t-[10px] border-neutral-50 px-4 py-5">
        <div className="mb-6 flex items-center justify-between gap-2">
          <h2 className="text-label-large font-medium">Your Skin Analysis routine</h2>
          <button
            type="button"
            className="cursor-pointer flex shrink-0 items-center gap-1 text-label-medium text-neutral-500"
            onClick={() => {
              track('section_click', { section_name: 'cart_skin_routine', page: 'cart', action: 'view_all' });
              open(
                'Your Skin Analysis routine',
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 px-4 pt-4 pb-6">{renderCards('grid')}</div>,
              );
            }}
          >
            view all <LucideIcon name="ChevronRight" size={20} />
          </button>
        </div>
        <CartHorizontalScroll className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-2 snap-x snap-proximity scroll-px-4 scrollbar-none">
          {renderCards()}
        </CartHorizontalScroll>
      </section>
    </CartSectionAnalytics>
  );
}
